/**
 * Centralized TanStack Query definitions. Components import the hooks; route
 * loaders import the query options. This keeps every cache key in one place.
 */

import type {
  FaceSummary,
  Goal,
  ProfileUpdate,
  ProtocolStep,
  ResonancePeak,
  Scan,
  Session,
  VoiceSummary,
  WellnessScores,
} from '@qi/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api-client.ts';

interface MeResponse {
  user: {
    id: string;
    email: string | null;
    name: string | null;
    plan: 'free' | 'premium';
  } | null;
  profile: {
    userId: string;
    goal: Goal | null;
    locale: string;
    consents: { analytics: boolean; improveQi: boolean; sessionReplay: boolean };
    fontScale: 'default' | 'large' | 'larger';
  } | null;
}

interface RecommendationResponse {
  protocol: ProtocolStep[];
  rationale: string;
}

export const meQueryOptions = queryOptions({
  queryKey: ['me'],
  queryFn: () => api.get<MeResponse>('/me'),
});

export const scansQueryOptions = queryOptions({
  queryKey: ['scans'],
  queryFn: () => api.get<{ scans: Scan[] }>('/scans'),
});

export const sessionsQueryOptions = queryOptions({
  queryKey: ['sessions'],
  queryFn: () => api.get<{ sessions: Session[] }>('/sessions'),
});

export function recommendationsQueryOptions(goal: Goal) {
  return queryOptions({
    queryKey: ['recommendations', goal],
    queryFn: () => api.post<RecommendationResponse>('/recommendations', { goal }),
  });
}

export interface InterpretInput {
  faceMetrics: FaceSummary;
  voiceMetrics: VoiceSummary;
  resonancePeaks: ResonancePeak[];
}

export function interpretScanQueryOptions(input: InterpretInput) {
  return queryOptions({
    // Cache key derived from the peaks so the same scan resolves once. The
    // store is cleared between scans, so the (unlikely) case of two scans with
    // identical top peaks but different face/voice metrics never overlaps here.
    queryKey: [
      'interpret',
      input.resonancePeaks.map((p) => `${p.frequencyId}:${p.score}`).join(','),
    ],
    queryFn: () => api.post<{ scores: WellnessScores }>('/interpret', input),
  });
}

export function useInterpretScan(input: InterpretInput) {
  return useQuery({
    ...interpretScanQueryOptions(input),
    enabled: input.resonancePeaks.length > 0,
  });
}

export function useMe() {
  return useQuery(meQueryOptions);
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (update: ProfileUpdate) =>
      api.patch<{ profile: MeResponse['profile'] }>('/me', update),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useScans() {
  return useQuery(scansQueryOptions);
}

export function useSessions() {
  return useQuery(sessionsQueryOptions);
}

export function useRecommendations(goal: Goal | null | undefined) {
  return useQuery({
    ...recommendationsQueryOptions(goal ?? 'stress'),
    enabled: !!goal,
  });
}

export function useCreateScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<Scan, 'id' | 'scores'>) => api.post<Scan>('/scans', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scans'] });
    },
  });
}
